/**
 * Weekly heart budget (Masco 2026-08-29 — the Hinge "rose", UN8's version).
 *
 * A heart is the unit of interest and it is SCARCE. Credits and hearts are two
 * separate economies and must stay separate: credits buy new *people* (the
 * 10 000 weekly swap), hearts spend on *a* person. Never charge credits for a
 * heart or the paywall stops being explainable.
 *
 *   free / guest  2 hearts per ISO week
 *   subscriber    5 hearts per ISO week
 *   vip           unlimited
 *
 * Unspent hearts do NOT roll over. Reset rides the same Monday 00:00 UTC ISO
 * week boundary as weeklyStore and newMessageWeekStore, so the copy says
 * "resets Monday", never "in seven days".
 *
 * Storage: un8.matching.heartWeek.v1
 */

import { writeLocal } from "../storage/localWrite";

const KEY = "un8.matching.heartWeek.v1";

type HeartWeek = {
  weekKey: string;
  /** Hearts spent this ISO week. */
  spent: number;
  /** Everyone ever hearted — a re-heart of the same person is free. */
  everHeartedIds: string[];
};

type MapFile = Record<string, HeartWeek>;

/** ISO week key: YYYY-Www (UTC Monday weeks). Same helper as weeklyStore. */
function weekKey(d: Date = new Date()): string {
  const date = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((date.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
  return `${date.getUTCFullYear()}-W${String(weekNo).padStart(2, "0")}`;
}

function readAll(): MapFile {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return {};
    return JSON.parse(raw) as MapFile;
  } catch {
    return {};
  }
}

function writeAll(map: MapFile) {
  writeLocal(KEY, JSON.stringify(map));
}

function ensure(userId: string, now = new Date()): HeartWeek {
  const map = readAll();
  const key = weekKey(now);
  let rec = map[userId];
  if (!rec) {
    rec = { weekKey: key, spent: 0, everHeartedIds: [] };
    map[userId] = rec;
    writeAll(map);
    return rec;
  }
  if (rec.weekKey !== key) {
    // New week. Budget resets; the lifetime list survives.
    rec = { weekKey: key, spent: 0, everHeartedIds: rec.everHeartedIds ?? [] };
    map[userId] = rec;
    writeAll(map);
  }
  if (!Array.isArray(rec.everHeartedIds)) {
    rec.everHeartedIds = [];
    map[userId] = rec;
    writeAll(map);
  }
  return rec;
}

export const HEART_WEEK_STORAGE_KEY = KEY;

export const heartWeekStore = {
  weekKey,

  /**
   * Hearts still available. `budget` is null for VIP (unlimited) — the store
   * returns null straight back so callers can render "unlimited" honestly
   * instead of a made-up large number.
   */
  remaining(userId: string, budget: number | null, now = new Date()): number | null {
    if (budget == null) return null;
    const rec = ensure(userId, now);
    return Math.max(0, budget - rec.spent);
  },

  /** Already hearted this person at some point — re-sending costs nothing. */
  hasHearted(userId: string, otherId: string, now = new Date()): boolean {
    return ensure(userId, now).everHeartedIds.includes(otherId);
  },

  canSend(userId: string, otherId: string, budget: number | null, now = new Date()): boolean {
    if (budget == null) return true;
    const rec = ensure(userId, now);
    if (rec.everHeartedIds.includes(otherId)) return true;
    return rec.spent < budget;
  },

  /**
   * Spend one heart on otherId. Idempotent per person: hearting someone you
   * have already hearted does not spend again.
   */
  spend(
    userId: string,
    otherId: string,
    budget: number | null,
    now = new Date()
  ): { ok: true; remaining: number | null } | { ok: false; error: "none_left" } {
    if (budget == null) return { ok: true, remaining: null };
    const map = readAll();
    const rec = ensure(userId, now);
    if (rec.everHeartedIds.includes(otherId)) {
      return { ok: true, remaining: Math.max(0, budget - rec.spent) };
    }
    if (rec.spent >= budget) return { ok: false, error: "none_left" };
    rec.spent += 1;
    rec.everHeartedIds = [...rec.everHeartedIds, otherId];
    map[userId] = rec;
    writeAll(map);
    return { ok: true, remaining: Math.max(0, budget - rec.spent) };
  },

  clearForUser(userId: string) {
    const map = readAll();
    delete map[userId];
    writeAll(map);
  },

  STORAGE_KEY: KEY,
};
